// Pixelate a block of RGBA pixels in place.
//
// Pulled out of EditImage so it can be tested without a canvas: jsdom has no 2D context, so the
// only way to know this is right was to look at a photo afterwards.
//
// It replaces a loop that called `ctx.getImageData(i, j, 1, 1)` once per 10px block — thousands of
// separate readbacks for a full-size photo, which is what made blurring crawl on a phone. That loop
// was also subtly wrong: it read back pixels it had already painted, so a block could sample the
// previous block's flat colour and the effect smeared along each row.
//
// `pixels` is a Uint8ClampedArray in RGBA order, `width` and `height` describe it, and alpha is
// left alone.
export const pixelateRegion = (pixels, width, height, sampleSize = 10) => {
  if (!pixels || width < 1 || height < 1 || sampleSize < 1) return pixels

  for (let blockY = 0; blockY < height; blockY += sampleSize) {
    for (let blockX = 0; blockX < width; blockX += sampleSize) {
      // The last block in a row or column is whatever is left over, never past the edge.
      const blockW = Math.min(sampleSize, width - blockX)
      const blockH = Math.min(sampleSize, height - blockY)

      let r = 0
      let g = 0
      let b = 0
      let count = 0
      for (let y = 0; y < blockH; y++) {
        for (let x = 0; x < blockW; x++) {
          const at = ((blockY + y) * width + (blockX + x)) * 4
          r += pixels[at]
          g += pixels[at + 1]
          b += pixels[at + 2]
          count++
        }
      }

      // The block's average rather than one sampled pixel: a cleaner result, and a stray highlight
      // cannot become the colour of the whole block.
      r = Math.round(r / count)
      g = Math.round(g / count)
      b = Math.round(b / count)

      for (let y = 0; y < blockH; y++) {
        for (let x = 0; x < blockW; x++) {
          const at = ((blockY + y) * width + (blockX + x)) * 4
          pixels[at] = r
          pixels[at + 1] = g
          pixels[at + 2] = b
        }
      }
    }
  }
  return pixels
}

export default pixelateRegion
